import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faPlus, faPrint, faXmark, faFloppyDisk, faFolderOpen, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import EvidencePreview from './evidence-preview.component';
import './evidence-creator.styles.scss';
import { UseCloudAdminFunction } from '../../../utils/firebase.utils';
import { useSelector } from 'react-redux';

const VARIABLES = [
  { key: 'fName', label: 'First Name' },
  { key: 'lName', label: 'Last Name' },
  { key: 'fullName', label: 'Full Name' },
  { key: 'startDate', label: 'Start Date' },
  { key: 'endDate', label: 'End Date' },
  { key: 'title', label: 'Title' },
  { key: 'presenters', label: 'Presenters' },
  { key: 'hours', label: 'Hours' },
  { key: 'userId', label: 'User ID' },
];

const DEFAULT_SAMPLE = {
  fName: 'Jane',
  lName: 'Smith',
  fullName: 'Jane Smith',
  startDate: '01/15/2026',
  endDate: '01/15/2026',
  title: 'Instructional Leadership Workshop',
  presenters: 'Dr. John Doe, Mary Johnson',
  hours: '3.0',
  userId: 'JS12345',
};

export const PRINT_WIDTH = 816;   // 8.5 inches at 96 DPI
export const PRINT_HEIGHT = 1056; // 11 inches at 96 DPI

const DIMS = {
  portrait:  { w: PRINT_WIDTH,  h: PRINT_HEIGHT },
  landscape: { w: PRINT_HEIGHT, h: PRINT_WIDTH  },
};

const ALIGN_TRANSFORM = {
  left: undefined,
  center: 'translateX(-50%)',
  right: 'translateX(-100%)',
};

let fieldIdSeq = 1;
let pageIdSeq = 1;

const makeBlankPage = () => ({ id: pageIdSeq++, bgImage: null, imgDims: { w: 1, h: 1 }, fields: [] });

// Created once at module load so both initial states share the same id reference.
const _firstPage = makeBlankPage();

const EvidenceCreator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userProfile = useSelector(state => state.userData.userProfile);
  const [pages, setPages] = useState([_firstPage]);
  const [currentPageId, setCurrentPageId] = useState(_firstPage.id);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState('edit');
  const [sampleData, setSampleData] = useState({ ...DEFAULT_SAMPLE });
  const [drag, setDrag] = useState(null);

  const [templateName, setTemplateName] = useState('');
  const [templateId, setTemplateId] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | 'error'

  const [orientation, setOrientation] = useState('portrait');
  const [scale, setScale] = useState(1);

  const canvasRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Derived ────────────────────────────────────────────────
  const currentPage = pages.find((p) => p.id === currentPageId) || pages[0];
  const { bgImage, imgDims, fields } = currentPage;
  const hasAnyImage = pages.some((p) => p.bgImage);
  const allFields = pages.flatMap((p) => p.fields);
  const usedVariables = VARIABLES.filter((v) => allFields.some((f) => f.variable === v.key));
  const { w: printW, h: printH } = DIMS[orientation];

  // ── Page helpers ───────────────────────────────────────────
  const updateCurrentPage = (updates) => {
    setPages((prev) => prev.map((p) => (p.id === currentPageId ? { ...p, ...updates } : p)));
  };

  const switchPage = (id) => {
    setCurrentPageId(id);
    setSelectedId(null);
    setDrag(null);
  };

  const addPage = () => {
    const page = makeBlankPage();
    setPages((prev) => [...prev, page]);
    switchPage(page.id);
  };

  const deletePage = (id) => {
    if (pages.length === 1) return;
    const idx = pages.findIndex((p) => p.id === id);
    const remaining = pages.filter((p) => p.id !== id);
    setPages(remaining);
    if (currentPageId === id) {
      switchPage(remaining[Math.min(idx, remaining.length - 1)].id);
    }
  };

  // ── Image loading ──────────────────────────────────────────
  const loadImageFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onloadend = async (e) => {
      const base64String = reader.result.split(',')[1];
        const uploadRes = await UseCloudAdminFunction(
          'uploadFileToStorage',
          {
            'fileDataBase64': base64String,
            'fileName': file.name,
            'fileType': file.type,
            'folder': userProfile.id,
            'userProfile': userProfile
          }
        )
        console.log(uploadRes);
        const src = uploadRes.url;
        const img = new Image();
        img.onload = () => {
          updateCurrentPage({ bgImage: src, imgDims: { w: img.naturalWidth, h: img.naturalHeight } });
          setSelectedId(null);
        };
        img.src = src;
    };
    reader.readAsDataURL(file);
  };

  // ── Field helpers ──────────────────────────────────────────
  const addField = (variable) => {
    const id = fieldIdSeq++;
    updateCurrentPage({
      fields: [
        ...fields,
        { id, variable: variable.key, label: variable.label, x: 10, y: 10,
          fontSize: 28, align: 'left', color: '#000000', bold: false, italic: false },
      ],
    });
    setSelectedId(id);
  };

  const updateField = (id, updates) => {
    updateCurrentPage({ fields: fields.map((f) => (f.id === id ? { ...f, ...updates } : f)) });
  };

  const removeField = (id) => {
    updateCurrentPage({ fields: fields.filter((f) => f.id !== id) });
    if (selectedId === id) setSelectedId(null);
  };

  // ── Drag ───────────────────────────────────────────────────
  const handleFieldMouseDown = (e, field) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(field.id);
    setDrag({ id: field.id, startMX: e.clientX, startMY: e.clientY, origX: field.x, origY: field.y });
  };

  const handleCanvasMouseMove = (e) => {
    if (!drag || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    updateField(drag.id, {
      x: Math.max(0, Math.min(99, drag.origX + ((e.clientX - drag.startMX) / rect.width) * 100)),
      y: Math.max(0, Math.min(99, drag.origY + ((e.clientY - drag.startMY) / rect.height) * 100)),
    });
  };

  const stopDrag = () => setDrag(null);

  // ── Template save / load ───────────────────────────────────
  const fetchTemplates = async () => {
    const res = await UseCloudAdminFunction('getEvidenceTemplates', {});
    if (res?.templates) setSavedTemplates(res.templates);
  };

  useEffect(() => { fetchTemplates(); }, []);

  // Pre-load a template passed via navigate() state (from the list page).
  useEffect(() => {
    const t = location.state?.template;
    if (t) loadTemplate(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Recompute scale whenever the canvas wrapper is mounted or the window resizes.
  // bgImage in deps ensures the effect re-runs when the canvas first appears.
  useEffect(() => {
    const el = canvasWrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (canvasWrapRef.current) setScale(canvasWrapRef.current.offsetWidth / printW);
    });
    ro.observe(el);
    setScale(el.offsetWidth / printW);
    return () => ro.disconnect();
  }, [bgImage, orientation]);

  const saveTemplate = async () => {
    if (!templateName.trim()) return;
    setSaveStatus('saving');
    const res = await UseCloudAdminFunction('saveEvidenceTemplate', {
      name: templateName.trim(),
      pages,
      orientation,
      ...(templateId ? { templateId } : {}),
    });
    if (res?.success) {
      if (!templateId) setTemplateId(res.id);
      setSaveStatus('saved');
      fetchTemplates();
      setTimeout(() => setSaveStatus(null), 2500);
    } else {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const loadTemplate = (template) => {
    setPages(template.pages);
    setCurrentPageId(template.pages[0]?.id ?? currentPageId);
    setTemplateName(template.name);
    setTemplateId(template.id);
    setOrientation(template.orientation || 'portrait');
    setSelectedId(null);
    setDrag(null);
    setMode('edit');
  };

  const selectedField = fields.find((f) => f.id === selectedId) || null;

  const fieldStyle = (field) => ({
    left: `${field.x}%`,
    top: `${field.y}%`,
    fontSize: field.fontSize,
    color: field.color,
    fontWeight: field.bold ? 'bold' : 'normal',
    fontStyle: field.italic ? 'italic' : 'normal',
    transform: ALIGN_TRANSFORM[field.align],
    cursor: 'move',
    outline: selectedId === field.id ? '2px solid #0066cc' : '1px dashed rgba(0,100,200,0.5)',
    background: selectedId === field.id ? 'rgba(200,220,255,0.25)' : 'rgba(255,255,255,0.1)',
    padding: '1px 3px',
  });

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="ec-root g-card">
      {/* Header */}
      <div className="ec-header">
        <div className="ec-header-left">
          <button className="ec-btn ec-btn-back" onClick={() => navigate('/admin/evidencecreator')}>
            <FontAwesomeIcon icon={faArrowLeft} /> Templates
          </button>
          <h2 className="ec-title">Evidence Template Creator</h2>
        </div>
        {hasAnyImage && (
          <div className="ec-tabs">
            <button
              className={`ec-tab${mode === 'edit' ? ' active' : ''}`}
              onClick={() => setMode('edit')}
            >
              Edit Template
            </button>
            <button
              className={`ec-tab${mode === 'preview' ? ' active' : ''}`}
              onClick={() => setMode('preview')}
            >
              Preview / Print
            </button>
          </div>
        )}
      </div>

      {/* Template toolbar */}
      <div className="ec-template-toolbar">
        <input
          type="text"
          className="uk-input ec-template-name"
          placeholder="Template name…"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
        />
        <button
          className="ec-btn ec-btn-save"
          disabled={!templateName.trim() || saveStatus === 'saving'}
          onClick={saveTemplate}
        >
          <FontAwesomeIcon icon={faFloppyDisk} />
          {saveStatus === 'saving' ? ' Saving…' : saveStatus === 'saved' ? ' Saved!' : saveStatus === 'error' ? ' Error' : ' Save Template'}
        </button>
        {savedTemplates.length > 0 && (
          <div className="ec-load-wrap">
            <FontAwesomeIcon icon={faFolderOpen} />
            <select
              className="uk-select ec-load-select"
              value=""
              onChange={(e) => {
                const t = savedTemplates.find((s) => s.id === e.target.value);
                if (t) loadTemplate(t);
              }}
            >
              <option value="" disabled>Load template…</option>
              {savedTemplates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Page strip + orientation (edit mode only) */}
      {mode === 'edit' && (
        <>
        <div className="ec-page-strip">
          {pages.map((page, index) => (
            <div
              key={page.id}
              className={`ec-page-thumb${page.id === currentPageId ? ' active' : ''}`}
              onClick={() => switchPage(page.id)}
            >
              {page.bgImage
                ? <img src={page.bgImage} className="ec-thumb-img" alt="" />
                : <div className="ec-thumb-empty"><FontAwesomeIcon icon={faImage} /></div>
              }
              <span className="ec-thumb-label">Page {index + 1}</span>
              {pages.length > 1 && (
                <button
                  className="ec-thumb-delete"
                  onClick={(e) => { e.stopPropagation(); deletePage(page.id); }}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              )}
            </div>
          ))}
          <button className="ec-page-add-btn" onClick={addPage}>
            <FontAwesomeIcon icon={faPlus} />
            <span>Add Page</span>
          </button>
        </div>
        <div className="ec-orient-toggle">
          <button
            className={`ec-orient-btn${orientation === 'portrait' ? ' active' : ''}`}
            onClick={() => setOrientation('portrait')}
          >
            <span className="ec-orient-icon ec-orient-portrait" />
            Portrait
          </button>
          <button
            className={`ec-orient-btn${orientation === 'landscape' ? ' active' : ''}`}
            onClick={() => setOrientation('landscape')}
          >
            <span className="ec-orient-icon ec-orient-landscape" />
            Landscape
          </button>
        </div>
        </>
      )}

      {/* Upload prompt for current page */}
      {mode === 'edit' && !bgImage && (
        <div className="ec-upload-zone" onClick={() => fileInputRef.current.click()}>
          <FontAwesomeIcon icon={faImage} />
          <p>Click to upload a background image</p>
          <span>PNG, JPG, GIF</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => loadImageFile(e.target.files[0])}
          />
        </div>
      )}

      {/* Editor */}
      {mode === 'edit' && bgImage && (
        <div className="ec-editor">
          {/* Left: variables */}
          <div className="ec-panel">
            <div className="ec-panel-title">Variables</div>
            <p className="ec-hint">Click to place on canvas</p>
            {VARIABLES.map((v) => (
              <button key={v.key} className="ec-var-btn" onClick={() => addField(v)}>
                <FontAwesomeIcon icon={faPlus} /> {v.label}
              </button>
            ))}
            <div className="ec-divider" />
            <button className="ec-btn ec-btn-outline" onClick={() => fileInputRef.current.click()}>
              Change Image
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => loadImageFile(e.target.files[0])}
            />
          </div>

          {/* Center: canvas — always rendered at PRINT_WIDTH×PRINT_HEIGHT, scaled to fit */}
          <div
            ref={canvasWrapRef}
            className="ec-canvas-wrap"
            style={{ height: printH * scale }}
          >
            <div
              ref={canvasRef}
              className="ec-canvas"
              style={{
                width: printW,
                height: printH,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                backgroundImage: `url(${bgImage})`,
                backgroundSize: '100% 100%',
              }}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={stopDrag}
              onMouseLeave={stopDrag}
              onClick={() => setSelectedId(null)}
            >
              {fields.map((field) => (
                <div
                  key={field.id}
                  className="ec-field"
                  style={fieldStyle(field)}
                  onMouseDown={(e) => handleFieldMouseDown(e, field)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {`{${field.label}}`}
                </div>
              ))}
            </div>
            <p className="ec-canvas-hint">
              Drag fields to reposition · click canvas background to deselect
            </p>
          </div>

          {/* Right: properties */}
          <div className="ec-panel">
            <div className="ec-panel-title">Properties</div>
            {selectedField ? (
              <>
                <div className="ec-prop-name">{selectedField.label}</div>
                <label className="ec-prop-row">
                  Font Size (print px)
                  <input
                    type="number"
                    className="uk-input"
                    value={selectedField.fontSize}
                    min={8}
                    max={200}
                    onChange={(e) => updateField(selectedField.id, { fontSize: Number(e.target.value) })}
                  />
                </label>
                <label className="ec-prop-row">
                  Alignment
                  <select
                    className="uk-select"
                    value={selectedField.align}
                    onChange={(e) => updateField(selectedField.id, { align: e.target.value })}
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </label>
                <label className="ec-prop-row">
                  Color
                  <input
                    type="color"
                    className="ec-color-pick"
                    value={selectedField.color}
                    onChange={(e) => updateField(selectedField.id, { color: e.target.value })}
                  />
                </label>
                <div className="ec-checks">
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedField.bold}
                      onChange={(e) => updateField(selectedField.id, { bold: e.target.checked })}
                    />
                    Bold
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedField.italic}
                      onChange={(e) => updateField(selectedField.id, { italic: e.target.checked })}
                    />
                    Italic
                  </label>
                </div>
                <button className="ec-btn ec-btn-danger" onClick={() => removeField(selectedField.id)}>
                  Remove Field
                </button>
              </>
            ) : (
              <p className="ec-hint">Select a field on the canvas to edit its properties</p>
            )}
          </div>
        </div>
      )}

      {/* Preview / Print */}
      {mode === 'preview' && (
        <div className="ec-preview-mode">
          <div className="ec-preview-controls no-print">
            <div>
              <div className="ec-panel-title" style={{ marginBottom: 8 }}>Preview Data</div>
              <div className="ec-sample-grid">
                {usedVariables.map((v) => (
                  <label key={v.key} className="ec-prop-row">
                    {v.label}
                    <input
                      type="text"
                      className="uk-input"
                      value={sampleData[v.key]}
                      onChange={(e) => setSampleData((prev) => ({ ...prev, [v.key]: e.target.value }))}
                    />
                  </label>
                ))}
              </div>
            </div>
            <button className="ec-btn ec-btn-print" onClick={() => window.print()}>
              <FontAwesomeIcon icon={faPrint} /> Print Document
            </button>
          </div>
          <EvidencePreview pages={pages} data={sampleData} orientation={orientation} />
        </div>
      )}
    </div>
  );
};

export default EvidenceCreator;
